import { useCallback, useEffect, useRef } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import styled from 'styled-components';
import I18n from 'I18n';
import getIn from 'transmute/getIn';
import { LoadingSpinner as FoundationsLoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import useContentSearch from '../../utils/useContentSearch';
import Raven from 'raven-js';
import { Typeahead } from 'foundations-components/transitional/components/Typeahead';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getHtmlBodyForOption(id, options) {
  return getIn(['snippet', 'htmlBody'], options.find(option => option.value === id));
}
function getVersion(options, id) {
  const option = options.find(o => o.value === id);
  return option && option.version;
}
const LoadingPositioner = styled.div.withConfig({
  displayName: "ContentTypeAheadContainer__LoadingPositioner",
  componentId: "sc-1tytj7k-0"
})(["align-items:center;display:flex;height:180px;justify-content:center;"]);
const LoadingSpinner = ({
  isLoading
}) => {
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingPositioner, {
      children: /*#__PURE__*/_jsx(FoundationsLoadingSpinner, {})
    });
  }
  return null;
};
function ContentTypeaheadContainer({
  ErrorState,
  EmptyState,
  onChange,
  onSearchChange,
  portalId,
  searchPlaceHolder,
  search,
  transformData,
  CustomItemComponent,
  contentKey
}) {
  const sessionStorageKey = contentKey ? `useContentSearch-savedQuery-${contentKey}` : null;
  const [__data, searchQuery, setSearchQuery, options, isLoading] = useContentSearch(search, transformData, onSearchChange, sessionStorageKey && sessionStorage.getItem(sessionStorageKey));
  const _inputRef = useRef(null);
  const prevOption = usePrevious(options);
  const wasLoading = usePrevious(isLoading);
  useEffect(() => {
    try {
      // @ts-expect-error if options isn't an array we'll assume that the typeahead hasn't displayed
      const didPreviouslyRenderTypeahead = prevOption && !!prevOption.length;
      // @ts-expect-error if options isn't an array we'll assume that the typeahead won't display
      const isTypeaheadRendered = options && !!options.length;
      if ((!didPreviouslyRenderTypeahead && isTypeaheadRendered || wasLoading && !isLoading) && _inputRef && _inputRef.current) {
        _inputRef.current.focus();
      }
    } catch (e) {
      Raven.captureException(Object.assign(new Error(`Failed to focus the input in ContentTypeAheadContainer: ${e}`), e), {
        extra: {
          error: e
        }
      });
    }
  }, [options, _inputRef, isLoading, wasLoading, prevOption]);
  const handleSearch = useCallback(e => {
    if (sessionStorageKey) {
      sessionStorage.setItem(sessionStorageKey, e.currentTarget.value);
    }
    setSearchQuery(e.currentTarget.value);
  }, [sessionStorageKey, setSearchQuery]);
  const handleChange = useCallback(e => {
    const id = e.target.value;
    if (typeof id === 'number' || typeof id === 'string') {
      const htmlBody = getHtmlBodyForOption(id, options);
      const version = getVersion(options, id);
      if (sessionStorageKey) {
        sessionStorage.removeItem(sessionStorageKey);
      }
      onChange({
        id,
        htmlBody,
        version
      });
    }
  }, [onChange, options, sessionStorageKey]);
  if (isLoading && !options) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      isLoading: isLoading
    });
  }
  if (!options) {
    return /*#__PURE__*/_jsx(ErrorState, {});
  } // @ts-expect-error if options isn't an array we'll assume that the typeahead hasn't displayed
  if (options.length === 0 && searchQuery.length === 0 && !isLoading) {
    return /*#__PURE__*/_jsx(EmptyState, {
      portalId: portalId
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Typeahead, {
      autoFilter: false,
      options: options,
      itemComponent: CustomItemComponent,
      placeholder: I18n.text(searchPlaceHolder),
      resultsClassName: "content-popover-search-results",
      searchClassName: "content-popover-search",
      onChange: handleChange,
      search: searchQuery,
      onSearchChange: handleSearch,
      inputRef: _inputRef,
      hideResults: isLoading
    }), /*#__PURE__*/_jsx(LoadingSpinner, {
      isLoading: isLoading
    })]
  });
}
export default ContentTypeaheadContainer;